import React, {useContext, useRef, useMemo} from 'react';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {addTemplateParticipant} from "../../../services/template/Participants";
import {getUsersTemplate} from "../../../services/template/Templates";
import {getTracks} from "../../../services/bill/BillingTracks";
import {AuthContext} from "../../auth/providers/AuthProvider";
import FormParticipants from "../forms/FormParticipants";
import {CsrfContext} from "../../../providers/CsrfProvider";
import {TemplateContext, TemplateProvider} from "../../../providers/TemplateProvider";
import {Typography} from "@mui/material";
import {ThemeContext} from "styled-components";

// Derive participants (users) from tracks; single source is GET /api/bills/templates/{id}/participants
function usersFromTracks(tracks) {
    if (!tracks || !Array.isArray(tracks)) return [];
    return tracks.filter(t => t.user_id && t.user).map(t => t.user);
}

const ParticipantsModel = ({ templateId, enabled = true, hydrating = false, onHydrated, expandedView = false, headerAction }) => {
    const { authClaims } = useContext(AuthContext);
    const { csrfToken } = useContext(CsrfContext);
    const {ownerSub, setOwnerSub}=useContext(TemplateContext)

    // Get bill data to access group_id
    const { data: billData } = useQuery(
        ['templates', String(templateId)],
        () => getUsersTemplate(String(templateId), csrfToken),
        {
            enabled: !!templateId && !!csrfToken,
        }
    );

    // Set ownerSub in context when billData is loaded
    React.useEffect(() => {
        if (billData?.owner_sub && setOwnerSub) {
            setOwnerSub(billData.owner_sub);
        }
    }, [billData?.owner_sub, setOwnerSub]);

    // Single GET: templates/{id}/participants (tracks with user); derive participants list from tracks
    const { data: tracks, isLoading: isQueryLoading, isError: isQueryError } = useQuery(
        ['billingTracks', String(templateId)],
        () => getTracks(String(templateId), csrfToken),
        {
            enabled: !!templateId && !!csrfToken && (enabled !== false),
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
        }
    );
    const users = useMemo(() => usersFromTracks(tracks), [tracks]);

    const hydratedRef = useRef(false);
    const prevTemplateIdRef = useRef(templateId);

    // Only report hydrated when the query was enabled and has settled (data or error), not when disabled or still loading.
    // Reset hydratedRef at the start of this effect when templateId changed so we never skip calling onHydrated due to effect order (hydration effect can run before the templateId effect).
    React.useEffect(() => {
        if (prevTemplateIdRef.current !== templateId) {
            prevTemplateIdRef.current = templateId;
            hydratedRef.current = false;
        }
        const wouldCall = onHydrated && enabled && !isQueryLoading && (tracks !== undefined || isQueryError) && !hydratedRef.current;
        if (wouldCall) {
            hydratedRef.current = true;
            onHydrated();
        }
    }, [enabled, isQueryLoading, isQueryError, tracks, onHydrated, templateId]);

    const theme = useContext(ThemeContext)

    // Mutation to manage data that updates
    const queryClient = useQueryClient();
    const isSubmittingRef = useRef(false);
    const lastSubmittedEmailRef = useRef('');
    
    const mutation = useMutation(addTemplateParticipant(csrfToken), {
        onSuccess: async () => {
            // Refetch tracks (single source for participants list) and rules
            await Promise.all([
                queryClient.refetchQueries(['billRules', String(templateId)]),
                queryClient.refetchQueries(['billingTracks', String(templateId)])
            ]);
            
            // Invalidate estimated splits (will auto-refetch when needed)
            queryClient.invalidateQueries(['estimatedSplits', String(templateId)]);
            
            // Also invalidate group participants query so dropdown updates
            if (billData?.group_id) {
                queryClient.invalidateQueries(['groupParticipantsExcludingBill', billData.group_id, templateId]);
            }
            
            // Reset submission tracking
            isSubmittingRef.current = false;
            lastSubmittedEmailRef.current = '';
        },
        onError: () => {
            // Reset submission tracking on error
            isSubmittingRef.current = false;
            lastSubmittedEmailRef.current = '';
        },
    });

    const handleParticipantRemoved = async () => {
        queryClient.invalidateQueries("templates");
        await queryClient.refetchQueries(['billingTracks', String(templateId)]);
        await queryClient.refetchQueries(['billRules', String(templateId)]);
        // Also invalidate group participants query so dropdown updates
        if (billData?.group_id) {
            queryClient.invalidateQueries(['groupParticipantsExcludingBill', billData.group_id, templateId]);
        }
    };

    const handleAddUser = (formData) => {
        const email = formData.email.trim();
        if (email !== '') {
            // Prevent duplicate submissions
            // Check if we're already submitting or if this is the same email we just submitted
            if (isSubmittingRef.current) {
                console.log('Already submitting, ignoring duplicate request');
                return;
            }
            
            // Check if this is the same email we just submitted (within last 2 seconds)
            const now = Date.now();
            if (lastSubmittedEmailRef.current === email.toLowerCase()) {
                console.log('Duplicate email submission detected, ignoring');
                return;
            }
            
            // Mark as submitting
            isSubmittingRef.current = true;
            lastSubmittedEmailRef.current = email.toLowerCase();
            
            formData.templateId = templateId;
            mutation.mutate(formData);
        }
    };

    if (!hydrating && isQueryLoading) {
        return <Typography sx={{color:theme.colors.text}}>Loading...</Typography>;
    }

    if (isQueryError) {
        console.error('Error fetching users:', isQueryError);
        return <Typography sx={{color:theme.colors.text}}>Error fetching participants, try refreshing</Typography>;
    }

    return (
        <>
            <FormParticipants 
                templateId={templateId} 
                participants={hydrating ? [] : users} 
                tracks={hydrating ? undefined : (tracks ?? [])}
                hydrating={hydrating}
                onSubmit={handleAddUser} 
                onParticipantRemoved={handleParticipantRemoved}
                groupId={billData?.group_id}
                expandedView={expandedView}
                headerAction={headerAction}
            />
        </>
    );
};

export default ParticipantsModel;
